<template>
  <div class="m-x-3 m-x-0:mo">
    <div class="m-b-2">
      <a
          v-if="hasQuery"
          @click="goBack"
          class="text-capitalize p-x-0 BLUE">
        <vk-icon
            class=""
            size="12"
            name="chevronleft"
            unbounded/>
        <span class="text-bold m-l-1 m-t-4">
          Back
        </span>
      </a>
    </div>

    <div class="d-flex jc-between">
      <div class="TITLE">{{ sectionTitle }}</div>
      <button v-if="showViewAll" class="VIEW-ALL" @click="toggleExpanded()">
        <span>
          View all
          <vk-icon
              class="CHEVRON cur-pointer c-depot"
              :class="{OPEN : expanded}"
              name="chevrondown"
              unbounded
          />
        </span>
      </button>
    </div>
    <div class="m-b-2">
      <span v-if="message">
        <img
            v-if="!isCompleted && !isInProgress "
            src="./../assets/Trophy.svg"
            class="m-r-1"/>
        {{ message }}
      </span>
    </div>
    <div v-if="noResults">
      <div class="d-flex fd-column ai-center p-t-2">
        <img class= "IMG" src="../assets/EmptySearch.svg"/>
        <span class="c-gray70">No results found. Please try again.</span>
      </div>
    </div>
    <transition v-if="!noResults && title" name="fade" mode="out-in" appears>
      <div v-if="fetching" class="d-flex" >
        <video-placeholder
            v-for="(index) in 3"
            :key="`placeholder-${index}`"
        />
      </div>

      <div v-else class="CARD-CONTAINER">
        <div v-for="(id, index) in idList" :key="index" class="CARD">
          <video-card :id="id" :showQuizButton="isInProgress" :showViewed="showViewed(id)"/>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import { formatDistanceToNow } from 'date-fns';
import capitalize from '@/filters/capitalize';
import VideoPlaceholder from './VideoPlaceholder';
import VideoCard from './VideoCard';


export default {
  name: 'video-collection',
  components: {
    VideoPlaceholder,
    VideoCard,
  },
  props: {
    ids: {
      type: [Array, String],
      default: () => [],
    },
    title: {
      type: String,
      required: true,
    },
  },
  data () {
    return {
      expanded: false,
      fetching: false,
    };
  },

  computed: {
    ...mapGetters('account/pro/education', ['videoList', 'videoRecords']),
    sortedIds () {
      // first sort by date
      if (this.ids && this.videoRecords) {
        const sortedArray = this.ids.map(id => (
          Number(id)))
          .sort((a, b) => new Date(this.videoList[a].createdDate)
            - new Date(this.videoList[b].createdDate));
        const watchedVideos = this.videoRecords.map(record => record.education_quiz_id);
        const updatedVideoArray = sortedArray.filter(video => !watchedVideos.includes(video));

        return this.hasQuery ? this.ids : updatedVideoArray;
      }
      return [];
    },
    sectionTitle () {
      return this.isSearch ? `"${this.title}"` : capitalize(this.title);
    },
    allWatched () {
      const watchedVideos = this.videoRecords
        .filter(record => this.ids.includes(record.education_quiz_id.toString())) || [];
      return watchedVideos.length === this.ids.length;
    },
    idList () {
      if (this.hasQuery) {
        return this.sortedIds;
      }
      return this.expanded ? this.sortedIds : this.sortedIds.slice(0, 3);
    },
    hasQuery () {
      return Object.keys(this.$route.query).length > 0;
    },
    isSearch () {
      return Object.prototype.hasOwnProperty.call(this.$route.query, 'search');
    },
    noResults () {
      return !!this.ids && this.ids.length < 1;
    },
    query () {
      return this.$route.query;
    },
    isCompleted () {
      return Object.prototype.hasOwnProperty.call(this.query, 'progress') && this.query.progress === 'Completed';
    },
    isInProgress () {
      return Object.prototype.hasOwnProperty.call(this.query, 'progress') && this.query.progress === 'In Progress';
    },
    isCategory () {
      return Object.prototype.hasOwnProperty.call(this.query, 'category');
    },
    message () {
      switch (true) {
        case Object.prototype.hasOwnProperty.call(this.query, 'progress'):
          return this.isCompleted
            ? 'Way to go!! You have watched and completed all these videos.'
            : 'Complete the quizzes for these videos to earn credit towards rewards.';
        case Object.prototype.hasOwnProperty.call(this.query, 'category') && this.allWatched:
          return 'Congratulations! You watched all videos for this category.';
        case this.allWatched && !this.isSearch:
          return 'Congratulations! You watched all videos for this Section. Check back for more content in the future.';
        default:
          return '';
      }
    },
    showViewAll () {
      return !this.noResults && this.idList.length > 3 && !this.hasQuery;
    },
  },
  watch: {
    ids: {
      immediate: true,
      handler (newVal, oldVal) {
        if (newVal !== oldVal) {
          this.updateFetching();
        }
      },
    },
  },
  mounted () {
    this.updateFetching();
  },
  methods: {
    updateFetching () {
      setTimeout(() => {
        this.fetching = false;
      }, 500);
      this.fetching = true;
    },
    toggleExpanded () {
      this.expanded = !this.expanded;
    },
    created (id) {
      return `Created ${formatDistanceToNow(new Date(this.videoList[id].createdDate))} ago`;
    },
    goBack () {
      this.$router.push({ name: 'dashboard::pro::education' });
    },
    showViewed (id) {
      return Object.prototype.hasOwnProperty.call(this.videoList[id], 'current_status');
    },
  },
};
</script>

<style lang="scss" scoped>
  @import "~@homeservices/design-kit/tools";

  @keyframes fade-in {
   0%{ opacity: 0;}
   100%{ opacity: 1;}
  }

  .VIEW-ALL {
    background-color: transparent;
    border: transparent solid 0px;
    color: $color-depot;
  }
  .CHEVRON {
    transform: rotate(0deg);
    transition: transform 0.5s ease;
  }
  .CHEVRON.OPEN {
    transform: rotate(180deg);
  }
  .BLUE {
    color: #286FAD;
  }
  .TITLE {
    font-weight: 600;
    font-size: 24px;

    @include mobile-only{
      font-size: 20px;
    }
  }
  .IMG {
    width:min-content;
  }
  .CARD {
    width: 288px;
    @include mobile-only {
      width: 100vw;

    }
  }
  .CARD-CONTAINER {
    display: flex;
    justify-content: start;
    gap: 0 24px;
    flex-wrap: wrap;
    @include mobile-only {
      justify-content: center;

    }
  }

</style>
